import { useState } from "react";
import type { OrganizationCapabilities, User } from "@lumen/shared";
import { AgentsPage } from "../../pages/agent-workspaces/AgentsPage";
import { AgentWorkspacesPage } from "../../pages/agent-workspaces/AgentWorkspacesPage";
import { AutomationsPage } from "../../pages/automations/AutomationsPage";
import { McpPage, SkillsPage } from "../../pages/integrations/ExtensionsPage";
import { IntegrationsPage } from "../../pages/integrations/IntegrationsPage";
import { ProvidersPage } from "../../pages/integrations/ProvidersPage";
import { t } from "../../i18n";

type ResourceKey =
  | "providers"
  | "skills"
  | "agents"
  | "integrations"
  | "mcp"
  | "workspaces"
  | "automations";

export function OrganizationResourcesPanel({
  permissions,
  capabilities,
  organizationId,
  siteOrganizationManagement = false,
  user,
}: {
  permissions: readonly string[];
  capabilities: OrganizationCapabilities;
  organizationId: string;
  siteOrganizationManagement?: boolean;
  user?: User;
}) {
  const [active, setActive] = useState<ResourceKey>("providers");
  const canManageOrganization = siteOrganizationManagement
    ? true
    : permissions.includes("organization:manage");
  const canManageSkills = siteOrganizationManagement
    ? true
    : permissions.includes("skills:write");
  const canUseIntegrations = siteOrganizationManagement
    ? true
    : permissions.includes("integrations:use");
  const canUseWorkspaces = siteOrganizationManagement
    ? true
    : permissions.includes("agent-workspaces:use");
  const items: Array<{ key: ResourceKey; label: string }> = [
    ...(capabilities.organizationProviders && canManageOrganization
      ? [{ key: "providers" as const, label: "Providers" }]
      : []),
    ...(canManageSkills
      ? [
          { key: "skills" as const, label: "Skills" },
          { key: "agents" as const, label: "Agents" },
        ]
      : []),
    ...(canUseIntegrations
      ? [{ key: "integrations" as const, label: "Integrations" }]
      : []),
    ...(!siteOrganizationManagement && permissions.includes("mcp:manage")
      ? [{ key: "mcp" as const, label: "MCP servers" }]
      : []),
    ...(canUseWorkspaces
      ? [{ key: "workspaces" as const, label: "Agent workspaces" }]
      : []),
    ...(!siteOrganizationManagement &&
    permissions.includes("automations:use") &&
    user
      ? [{ key: "automations" as const, label: "Automations" }]
      : []),
  ];
  const selected = items.some((item) => item.key === active)
    ? active
    : items[0]?.key;

  if (!selected) {
    return (
      <section className="panel p-5">
        <h2 className="font-bold">{t("Organization resources")}</h2>
        <p className="mt-1 text-sm text-stone-500">
          {t("You do not have access to manage organization resources.")}
        </p>
      </section>
    );
  }

  return (
    <div className="space-y-5">
      <nav
        aria-label={t("Organization resources")}
        className="flex flex-wrap gap-2 rounded-xl border border-stone-200 bg-white p-2 dark:border-white/10 dark:bg-white/3"
      >
        {items.map((item) => (
          <button
            key={item.key}
            className={selected === item.key ? "button-primary" : "button"}
            onClick={() => setActive(item.key)}
          >
            {t(item.label)}
          </button>
        ))}
      </nav>
      {selected === "providers" && (
        <ProvidersPage
          canWrite={canManageOrganization}
          scope="organization"
          organizationId={
            siteOrganizationManagement ? organizationId : undefined
          }
          embedded
        />
      )}
      {selected === "skills" && (
        <SkillsPage
          canManageOrganization={canManageSkills}
          canManagePlatform={false}
          organizationId={organizationId}
          canManageSiteOrganization={siteOrganizationManagement}
        />
      )}
      {selected === "agents" && (
        <AgentsPage
          canManageOrganization={canManageSkills}
          canManagePlatform={false}
          organizationId={organizationId}
          canManageSiteOrganization={siteOrganizationManagement}
        />
      )}
      {selected === "integrations" && user && (
        <IntegrationsPage
          user={user}
          organizationId={
            siteOrganizationManagement ? organizationId : undefined
          }
          siteOrganizationManagement={siteOrganizationManagement}
          organizationOnly
        />
      )}
      {selected === "mcp" && <McpPage canManage organizationOnly />}
      {selected === "workspaces" && (
        <AgentWorkspacesPage
          canManage={canUseWorkspaces}
          organizationId={organizationId}
          siteOrganizationManagement={siteOrganizationManagement}
        />
      )}
      {selected === "automations" && user && (
        <AutomationsPage user={user} organizationOnly />
      )}
    </div>
  );
}
